/**
 * NewRelic tracking utilities for Modern UI components
 * Provides comprehensive user interaction tracking with viewport detection,
 * dark mode awareness, and user context enrichment for Modern UI features.
 *
 * @fileoverview All tracking functions automatically include viewport size,
 * dark mode state, and timestamp metadata for enhanced analytics. Supports
 * tracking page views, user interactions, and engagement metrics across the
 * Modern UI component library (Incidents, Impact Reports, etc.).
 *
 * ## Event Data Structures
 *
 * ### ModernUI.Incidents.View Event
 * ```javascript
 * {
 *   eventType: "ModernUI.Incidents.View",
 *   viewport: "mobile" | "tablet" | "desktop",
 *   darkMode: true | false,
 *   timestamp: 1707628800000,
 *   incidentCount: 5,
 *   hasFilters: false
 * }
 * ```
 *
 * ### ModernUI.Incident.Expand Event
 * ```javascript
 * {
 *   eventType: "ModernUI.Incident.Expand",
 *   incidentId: "PRBT12345",
 *   source: "card" | "row",
 *   section: "active_incidents" | "past_incidents",
 *   viewport: "mobile" | "tablet" | "desktop",
 *   darkMode: true | false,
 *   timestamp: 1707628800000
 * }
 * ```
 *
 * ### ModernUI.Timeline.Expand Event
 * ```javascript
 * {
 *   eventType: "ModernUI.Timeline.Expand",
 *   incidentId: "PRBT12345",
 *   viewport: "mobile" | "tablet" | "desktop",
 *   darkMode: true | false,
 *   timestamp: 1707628800000,
 *   totalEntries: 12,
 *   visibleEntries: 5,
 *   expandedEntries: 12,
 *   action: "expand" | "collapse"
 * }
 * ```
 *
 * ### ModernUI.ViewFullDetails Event
 * ```javascript
 * {
 *   eventType: "ModernUI.ViewFullDetails",
 *   incidentId: "PRBT12345",
 *   viewport: "mobile" | "tablet" | "desktop",
 *   darkMode: true | false,
 *   timestamp: 1707628800000,
 *   source: "expanded_content" | "incident_card"
 * }
 * ```
 *
 * @version 1.0.0 - Initial release for Modern UI tracking
 */

/**
 * Get current viewport size classification
 *
 * Classifies the current browser window width into responsive breakpoints
 * matching the Modern UI design system (375px mobile, 768px tablet, 1920px desktop).
 *
 * @returns {"mobile"|"tablet"|"desktop"} Current viewport classification
 * @example
 * const viewport = getViewport();
 * // Returns "mobile" for widths < 768px
 * // Returns "tablet" for widths 768px - 1023px
 * // Returns "desktop" for widths >= 1024px
 */
export const getViewport = () => {
  if (typeof window === 'undefined') return 'desktop'; // SSR fallback

  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

/**
 * Check if dark mode is currently active
 *
 * Detects dark mode by checking the data-theme attribute on the document root.
 * Supports the standard dark mode implementation used across Modern UI components.
 *
 * @returns {boolean} True if dark mode is active, false otherwise
 * @example
 * const isDark = isDarkMode();
 * // Returns true if <html data-theme="dark">
 */
export const isDarkMode = () => {
  if (typeof document === 'undefined') return false; // SSR fallback

  return document.documentElement.getAttribute('data-theme') === 'dark';
};

/**
 * Track Modern UI component interaction
 *
 * Generic tracking function for all Modern UI interactions. Automatically enriches
 * events with viewport size, dark mode state, and timestamp. Uses NewRelic's
 * addPageAction API for low-latency event tracking.
 *
 * @param {string} action - Page action name (e.g., "ModernUI.Incident.Expand")
 * @param {Object} metadata - Additional event-specific metadata
 * @param {string} metadata.incidentId - Incident number (optional)
 * @param {number} metadata.incidentCount - Number of incidents displayed (optional)
 * @param {Object} newrelic - NewRelic browser agent instance (optional, uses window.newrelic if not provided)
 *
 * @example
 * // Track incident expansion
 * trackModernUIInteraction('ModernUI.Incident.Expand', {
 *   incidentId: 'PRBT12345',
 *   source: 'card',
 *   section: 'past_incidents'
 * });
 *
 * @example
 * // Track page view with incident count
 * trackModernUIInteraction('ModernUI.Incidents.View', {
 *   incidentCount: 5,
 *   hasFilters: false
 * });
 */
export const trackModernUIInteraction = (action, metadata = {}, newrelic = null) => {
  const agent = newrelic || (typeof window !== 'undefined' ? window.newrelic : null);

  if (!agent || !agent.addPageAction) {
    console.debug('NewRelic tracking: Agent not available or disabled');
    return;
  }

  if (!action) {
    console.warn('NewRelic tracking: Missing action parameter');
    return;
  }

  try {
    const enrichedMetadata = {
      ...metadata,
      viewport: getViewport(),
      darkMode: isDarkMode(),
      pageUrl: window.location.href,
      timestamp: Date.now()
    };

    agent.addPageAction(action, enrichedMetadata);

    console.debug(`NewRelic tracking: ${action}`, enrichedMetadata);
  } catch (error) {
    console.error('NewRelic tracking error:', error);
  }
};

/**
 * Track incident section page view
 *
 * Records when a user views the incidents section/page. Includes count of
 * incidents displayed and any active filters. Should be called on component
 * mount or when incident list changes significantly.
 *
 * User identity is attached globally via setUserId in AppWrapper — no
 * userProfile parameter needed here.
 *
 * @param {number} incidentCount - Number of incidents currently displayed
 * @param {Object} options - Additional tracking options
 * @param {boolean} options.hasFilters - Whether any filters are active
 * @param {string} options.sortBy - Current sort field (optional)
 * @param {string} options.sortOrder - Current sort order "asc"|"desc" (optional)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track page view with 5 incidents, no filters
 * trackIncidentSectionView(5, { hasFilters: false });
 *
 * @example
 * // Track page view with filters and sorting
 * trackIncidentSectionView(3, {
 *   hasFilters: true,
 *   sortBy: 'priority',
 *   sortOrder: 'desc'
 * });
 */
export const trackIncidentSectionView = (incidentCount, options = {}, newrelic = null) => {
  const metadata = {
    incidentCount,
    hasFilters: options.hasFilters ?? false,
    uiType: 'modern',
    ...(options.sortBy != null && { sortBy: options.sortBy }),
    ...(options.sortOrder != null && { sortOrder: options.sortOrder }),
  };

  trackModernUIInteraction('ModernUI.Incidents.View', metadata, newrelic);
};

/**
 * Track incident detail page view
 *
 * Records when a user navigates to a specific incident's detail page in the
 * Modern UI. Fires on mount and on each problem ID change. Does not fire
 * if problemId is null or undefined.
 *
 * User identity is attached globally via setUserId in AppWrapper — no
 * userProfile parameter needed here.
 *
 * @param {string} problemId - Problem identifier (e.g., "PRBT12345")
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * trackIncidentDetailPageView('PRBT12345');
 */
export const trackIncidentDetailPageView = (problemId, newrelic = null) => {
  if (!problemId) {
    return;
  }

  const metadata = {
    problemId,
    uiType: 'modern'
  };

  trackModernUIInteraction('ModernUI.Incidents.DetailView', metadata, newrelic);
};

/**
 * Track incident card/row expansion
 *
 * Records when a user expands an incident card (mobile) or row (desktop) to view
 * additional details. Fires synchronously on the user click — incident detail
 * data (timeline, etc.) is generally not yet loaded at this point, so this
 * event records the intent-to-expand, not the loaded content.
 *
 * @param {string} incidentId - Incident number (e.g., "PRBT12345")
 * @param {Object} options - Additional tracking options
 * @param {"card"|"row"} options.source - Expansion source
 * @param {"active_incidents"|"past_incidents"} options.section - List context
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * trackIncidentExpansion('PRBT12345', { source: 'card', section: 'past_incidents' });
 */
export const trackIncidentExpansion = (incidentId, options = {}, newrelic = null) => {
  if (!incidentId) {
    console.warn('NewRelic tracking: Missing incidentId for expansion tracking');
    return;
  }

  trackModernUIInteraction('ModernUI.Incident.Expand', {
    incidentId,
    source: options.source ?? 'card',
    section: options.section ?? 'active_incidents',
  }, newrelic);
};

/**
 * Track incident card/row collapse
 *
 * Records when a user collapses an expanded incident card or row.
 *
 * @param {string} incidentId - Incident number (e.g., "PRBT12345")
 * @param {Object} options - Additional tracking options
 * @param {number} options.viewDurationMs - How long the incident was expanded (optional)
 * @param {"card"|"row"} options.source - Collapse source (optional)
 * @param {"active_incidents"|"past_incidents"} options.section - List context (defaults to "active_incidents" if omitted)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track card collapse after 5 seconds
 * trackIncidentCollapse('PRBT12345', {
 *   viewDurationMs: 5000,
 *   source: 'card',
 *   section: 'past_incidents'
 * });
 */
export const trackIncidentCollapse = (incidentId, options = {}, newrelic = null) => {
  if (!incidentId) {
    console.warn('NewRelic tracking: Missing incidentId for collapse tracking');
    return;
  }

  trackModernUIInteraction('ModernUI.Incident.Collapse', {
    incidentId,
    viewDurationMs: options.viewDurationMs ?? 0,
    source: options.source ?? 'card',
    section: options.section ?? 'active_incidents',
  }, newrelic);
};

/**
 * Track timeline expansion/collapse
 *
 * Records when a user expands or collapses the timeline section within an
 * incident's expanded content. Tracks the number of entries revealed/hidden.
 *
 * @param {string} incidentId - Incident number (e.g., "PRBT12345")
 * @param {Object} options - Additional tracking options
 * @param {number} options.totalEntries - Total number of timeline entries
 * @param {number} options.visibleEntries - Number of initially visible entries
 * @param {number} options.expandedEntries - Number of entries after expansion
 * @param {"expand"|"collapse"} options.action - Whether expanding or collapsing
 * @param {"active_incidents"|"past_incidents"} options.section - List context (defaults to "active_incidents" if omitted)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track timeline expansion (showing 7 more entries)
 * trackTimelineExpansion('PRBT12345', {
 *   totalEntries: 12,
 *   visibleEntries: 5,
 *   expandedEntries: 12,
 *   action: 'expand',
 *   section: 'past_incidents'
 * });
 *
 * @example
 * // Track timeline collapse
 * trackTimelineExpansion('PRBT12345', {
 *   totalEntries: 12,
 *   visibleEntries: 5,
 *   expandedEntries: 5,
 *   action: 'collapse',
 *   section: 'active_incidents'
 * });
 */
export const trackTimelineExpansion = (incidentId, options = {}, newrelic = null) => {
  if (!incidentId) {
    console.warn('NewRelic tracking: Missing incidentId for timeline expansion tracking');
    return;
  }

  trackModernUIInteraction('ModernUI.Timeline.Expand', {
    incidentId,
    totalEntries: options.totalEntries ?? 0,
    visibleEntries: options.visibleEntries ?? 0,
    expandedEntries: options.expandedEntries ?? 0,
    action: options.action ?? 'expand',
    section: options.section ?? 'active_incidents',
  }, newrelic);
};

/**
 * Track "View Full Details" link clicks
 *
 * Records when a user clicks the "View Full Details" link to navigate to the
 * full incident details page. Tracks the source of the click for navigation
 * pattern analysis.
 *
 * @param {string} incidentId - Incident number (e.g., "PRBT12345")
 * @param {Object} options - Additional tracking options
 * @param {string} options.source - Click source "expanded_content"|"incident_card"|"incident_row"
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track navigation from expanded content
 * trackViewFullDetails('PRBT12345', {
 *   source: 'expanded_content'
 * });
 *
 * @example
 * // Track navigation from incident card
 * trackViewFullDetails('PRBT12345', {
 *   source: 'incident_card'
 * });
 */
export const trackViewFullDetails = (incidentId, options = {}, newrelic = null) => {
  if (!incidentId) {
    console.warn('NewRelic tracking: Missing incidentId for view full details tracking');
    return;
  }

  trackModernUIInteraction('ModernUI.ViewFullDetails', {
    incidentId,
    source: options.source ?? 'expanded_content'
  }, newrelic);
};

/**
 * Track an Eternium member attaching, replacing or removing an incident's Exec Summary or
 * PIR link (US2163904). The link itself is not sent, only which kind changed.
 *
 * @param {string} incidentId - Problem number
 * @param {string} kind - "exec_summary"|"pir"
 * @param {string} action - "save"|"clear"
 * @param {string} outcome - "ok"|"error"
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 */
export const trackIncidentArtifactChange = (incidentId, kind, action, outcome, newrelic = null) => {
  if (!incidentId) {
    console.warn('NewRelic tracking: Missing incidentId for incident artifact tracking');
    return;
  }

  trackModernUIInteraction('ModernUI.IncidentArtifact.Change', {
    incidentId,
    kind,
    action,
    outcome
  }, newrelic);
};

/**
 * Track incident filter/sort changes
 *
 * Records when a user changes filters or sort settings on the incidents page.
 * Helps understand how users prefer to organize and view incident data.
 *
 * @param {Object} options - Filter/sort tracking options
 * @param {string} options.filterType - Type of filter changed "priority"|"status"|"brand"|"search"
 * @param {string} options.filterValue - New filter value
 * @param {string} options.sortBy - Sort field (optional)
 * @param {string} options.sortOrder - Sort order "asc"|"desc" (optional)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track priority filter change
 * trackIncidentFilterChange({
 *   filterType: 'priority',
 *   filterValue: 'EP1'
 * });
 *
 * @example
 * // Track sort change
 * trackIncidentFilterChange({
 *   sortBy: 'incidentStart',
 *   sortOrder: 'desc'
 * });
 */
export const trackIncidentFilterChange = (options = {}, newrelic = null) => {
  trackModernUIInteraction('ModernUI.Incidents.FilterChange', {
    filterType: options.filterType ?? null,
    filterValue: options.filterValue ?? null,
    sortBy: options.sortBy ?? null,
    sortOrder: options.sortOrder ?? null
  }, newrelic);
};

/**
 * Track system status page view
 *
 * Records when a user views the system status/build information page. Includes
 * build metadata such as region, branch, and commit hash for deployment tracking.
 *
 * @param {Object} metadata - Build information metadata
 * @param {string} metadata.region - AWS region (e.g., "us-east-1")
 * @param {string} metadata.buildBranch - Git branch name
 * @param {string} metadata.buildHash - Git commit hash
 * @param {string} metadata.buildDate - Build timestamp
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track status page view with build info
 * trackSystemStatusView({
 *   region: 'us-east-1',
 *   buildBranch: 'main',
 *   buildHash: 'abc123def456',
 *   buildDate: '2025-02-11T12:00:00Z'
 * });
 */
export const trackSystemStatusView = (metadata = {}, newrelic = null) => {
  trackModernUIInteraction('System.Status.View', {
    ...metadata
  }, newrelic);
};

/**
 * Track build info commit link clicks
 *
 * Records when a user clicks the commit hash link to view the full commit details
 * on GitHub Enterprise. Helps understand engagement with build transparency features.
 *
 * @param {Object} metadata - Commit link metadata
 * @param {string} metadata.buildHash - Git commit hash that was clicked
 * @param {string} metadata.region - AWS region
 * @param {string} metadata.buildBranch - Git branch name
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track commit link click
 * trackBuildInfoView({
 *   buildHash: 'abc123def456',
 *   region: 'us-east-1',
 *   buildBranch: 'main'
 * });
 */
export const trackBuildInfoView = (metadata = {}, newrelic = null) => {
  trackModernUIInteraction('System.BuildInfo.View', {
    ...metadata
  }, newrelic);
};

/**
 * Track profile page view
 *
 * Records when a user views their profile page. Includes counts of total
 * available features and currently enabled features for usage pattern analysis.
 *
 * @param {Object} metadata - Profile view metadata
 * @param {number} metadata.totalFeatures - Total number of available features
 * @param {number} metadata.enabledFeatures - Number of currently enabled features
 * @param {string[]} metadata.enabledFeatureNames - Names of enabled features (optional)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track profile view
 * trackProfileView({
 *   totalFeatures: 10,
 *   enabledFeatures: 3,
 *   enabledFeatureNames: ['modernUI', 'darkMode', 'experimentalFeature']
 * });
 */
export const trackProfileView = (metadata = {}, newrelic = null) => {
  trackModernUIInteraction('Profile.View', {
    totalFeatures: metadata.totalFeatures ?? 0,
    enabledFeatures: metadata.enabledFeatures ?? 0,
    enabledFeatureNames: metadata.enabledFeatureNames ?? []
  }, newrelic);
};

/**
 * Track profile feature toggle
 *
 * Records when a user enables or disables a feature flag. Tracks the feature key
 * and new value for adoption and usage analysis.
 *
 * @param {string} featureKey - Feature flag key (e.g., "modernUI", "darkMode")
 * @param {boolean} newValue - New feature state (true = enabled, false = disabled)
 * @param {Object} metadata - Additional tracking metadata
 * @param {string} metadata.featureDisplayName - Human-readable feature name (optional)
 * @param {string} metadata.category - Feature category (optional)
 * @param {boolean} metadata.isExperimental - Whether feature is experimental (optional)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track feature enable
 * trackProfileFeatureToggle('modernUI', true, {
 *   featureDisplayName: 'Modern UI',
 *   category: 'interface',
 *   isExperimental: false
 * });
 *
 * @example
 * // Track feature disable
 * trackProfileFeatureToggle('darkMode', false, {
 *   featureDisplayName: 'Dark Mode',
 *   category: 'interface'
 * });
 */
export const trackProfileFeatureToggle = (featureKey, newValue, metadata = {}, newrelic = null) => {
  if (!featureKey) {
    console.warn('NewRelic tracking: Missing featureKey for feature toggle tracking');
    return;
  }

  const action = newValue ? 'Profile.FeatureToggle.Enable' : 'Profile.FeatureToggle.Disable';
  trackModernUIInteraction(action, {
    featureKey,
    featureDisplayName: metadata.featureDisplayName ?? featureKey,
    category: metadata.category ?? 'unknown',
    isExperimental: metadata.isExperimental ?? false
  }, newrelic);
};

/**
 * Track profile feature toggle error
 *
 * Records when a feature toggle operation fails. Tracks the error type and context
 * for troubleshooting and reliability analysis.
 *
 * @param {string} featureKey - Feature flag key that failed to toggle
 * @param {boolean} attemptedValue - Value that was attempted to be set
 * @param {Object} metadata - Error metadata
 * @param {string} metadata.errorType - Type of error (e.g., "rate_limit", "permission_denied")
 * @param {string} metadata.errorMessage - Error message text (optional)
 * @param {string} metadata.featureDisplayName - Human-readable feature name (optional)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track rate limit error
 * trackProfileFeatureToggleError('modernUI', true, {
 *   errorType: 'rate_limit',
 *   errorMessage: 'Too many requests',
 *   featureDisplayName: 'Modern UI'
 * });
 *
 * @example
 * // Track permission error
 * trackProfileFeatureToggleError('adminFeature', true, {
 *   errorType: 'permission_denied',
 *   errorMessage: 'Access denied',
 *   featureDisplayName: 'Admin Feature'
 * });
 */
export const trackProfileFeatureToggleError = (featureKey, attemptedValue, metadata = {}, newrelic = null) => {
  if (!featureKey) {
    console.warn('NewRelic tracking: Missing featureKey for feature toggle error tracking');
    return;
  }

  trackModernUIInteraction('Profile.FeatureToggle.Error', {
    featureKey,
    attemptedValue,
    errorType: metadata.errorType ?? 'unknown',
    errorMessage: metadata.errorMessage ?? '',
    featureDisplayName: metadata.featureDisplayName ?? featureKey
  }, newrelic);
};

/**
 * Track profile feature reset
 *
 * Records when a user resets all feature flags to defaults. Tracks the number
 * of features reset and whether the operation succeeded or failed.
 *
 * @param {Object} metadata - Reset operation metadata
 * @param {number} metadata.totalFeatures - Total number of features that were reset
 * @param {number} metadata.successCount - Number of features successfully reset
 * @param {number} metadata.failureCount - Number of features that failed to reset
 * @param {string[]} metadata.failedFeatures - Names of features that failed (optional)
 * @param {boolean} metadata.success - Whether the overall operation succeeded
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track successful reset
 * trackProfileFeatureReset({
 *   totalFeatures: 5,
 *   successCount: 5,
 *   failureCount: 0,
 *   success: true
 * });
 *
 * @example
 * // Track partial failure
 * trackProfileFeatureReset({
 *   totalFeatures: 5,
 *   successCount: 3,
 *   failureCount: 2,
 *   failedFeatures: ['feature1', 'feature2'],
 *   success: false
 * });
 */
export const trackProfileFeatureReset = (metadata = {}, newrelic = null) => {
  trackModernUIInteraction('Profile.FeatureReset', {
    totalFeatures: metadata.totalFeatures ?? 0,
    successCount: metadata.successCount ?? 0,
    failureCount: metadata.failureCount ?? 0,
    failedFeatures: metadata.failedFeatures ?? [],
    success: metadata.success ?? false
  }, newrelic);
};

/**
 * Track profile feature search
 *
 * Records when a user searches for feature flags on the profile page. Tracks
 * search terms and result counts to understand feature discoverability.
 *
 * @param {string} searchTerm - User's search query
 * @param {number} resultCount - Number of features matching the search
 * @param {Object} metadata - Additional search metadata
 * @param {number} metadata.totalFeatures - Total number of available features (optional)
 * @param {boolean} metadata.hasResults - Whether search returned any results (optional)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track search with results
 * trackProfileFeatureSearch('modern', 3, {
 *   totalFeatures: 10,
 *   hasResults: true
 * });
 *
 * @example
 * // Track search with no results
 * trackProfileFeatureSearch('xyz', 0, {
 *   totalFeatures: 10,
 *   hasResults: false
 * });
 */
export const trackProfileFeatureSearch = (searchTerm, resultCount, metadata = {}, newrelic = null) => {
  trackModernUIInteraction('Profile.FeatureSearch', {
    searchTerm: searchTerm ?? '',
    resultCount: resultCount ?? 0,
    totalFeatures: metadata.totalFeatures ?? 0,
    hasResults: (resultCount ?? 0) > 0
  }, newrelic);
};

/**
 * Track configuration page view
 *
 * Records when a user views the configuration page with context about
 * active tab and user privileges.
 *
 * @param {Object} metadata - Configuration view metadata
 * @param {string} metadata.activeTab - Active configuration tab
 * @param {boolean} metadata.hasWriteAccess - Whether user has write privileges
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * trackConfigurationView({
 *   activeTab: 'delivery-streams',
 *   hasWriteAccess: true
 * });
 */
export const trackConfigurationView = (metadata = {}, newrelic = null) => {
  // viewport is auto-enriched centrally by trackModernUIInteraction — no need to pass.
  trackModernUIInteraction('Config.View', { ...metadata }, newrelic);
};

/**
 * Track configuration tab switch
 *
 * Records when a user switches between configuration tabs to understand
 * navigation patterns and feature usage.
 *
 * @param {string} fromTab - Previous tab name
 * @param {string} toTab - New tab name
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * trackConfigurationTabSwitch('delivery-streams', 'brands');
 */
export const trackConfigurationTabSwitch = (fromTab, toTab, newrelic = null) => {
  trackModernUIInteraction('Config.TabSwitch', { fromTab, toTab }, newrelic);
};

/**
 * Track delivery stream operations
 *
 * Records CRUD operations on delivery streams with success/failure tracking.
 *
 * @param {string} operation - Operation type "Create"|"Edit"|"Delete"|"View"|"Search"|"FilterToggle"
 * @param {Object} metadata - Operation-specific metadata
 * @param {string} metadata.streamId - Delivery stream ID (optional)
 * @param {string} metadata.streamName - Delivery stream name (optional)
 * @param {string} metadata.searchType - Search type "product"|"commander"|"leadership" (optional)
 * @param {string} metadata.searchTerm - Search term (optional)
 * @param {number} metadata.resultCount - Search result count (optional)
 * @param {boolean} metadata.success - Operation success status (optional)
 * @param {string} metadata.error - Error message (optional)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track successful creation
 * trackDeliveryStreamOperation('Create', {
 *   streamId: 'ds-123',
 *   streamName: 'Production Stream',
 *   success: true
 * });
 *
 * @example
 * // Track search operation
 * trackDeliveryStreamOperation('Search', {
 *   searchType: 'product',
 *   searchTerm: 'AutoTrader',
 *   resultCount: 5
 * });
 */
export const trackDeliveryStreamOperation = (operation, metadata = {}, newrelic = null) => {
  trackModernUIInteraction(`Config.DeliveryStream.${operation}`, { ...metadata }, newrelic);
};

/**
 * Track brand operations
 *
 * Records CRUD operations on brands with success/failure tracking.
 *
 * @param {string} operation - Operation type "Create"|"Edit"|"Delete"|"View"|"ModalOpen"|"ModalClose"
 * @param {Object} metadata - Operation-specific metadata
 * @param {string} metadata.brandId - Brand ID (optional)
 * @param {string} metadata.brandName - Brand name (optional)
 * @param {boolean} metadata.success - Operation success status (optional)
 * @param {string} metadata.error - Error message (optional)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track successful brand creation
 * trackBrandOperation('Create', {
 *   brandId: 'brand-123',
 *   brandName: 'AutoTrader',
 *   success: true
 * });
 *
 * @example
 * // Track modal open
 * trackBrandOperation('ModalOpen', {
 *   mode: 'create'
 * });
 */
export const trackBrandOperation = (operation, metadata = {}, newrelic = null) => {
  trackModernUIInteraction(`Config.Brand.${operation}`, { ...metadata }, newrelic);
};

/**
 * Track leadership search operations
 *
 * Records leadership responder search with result counts and PagerDuty link clicks.
 *
 * @param {string} searchTerm - Search term used
 * @param {number} resultCount - Number of results found
 * @param {Object} metadata - Additional metadata (optional)
 * @param {boolean} metadata.success - Search success status (optional)
 * @param {string} metadata.error - Error message (optional)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * trackLeadershipSearch('John Doe', 3, {
 *   success: true
 * });
 */
export const trackLeadershipSearch = (searchTerm, resultCount, metadata = {}, newrelic = null) => {
  trackModernUIInteraction('Config.LeadershipSearch.Search', {
    searchTerm,
    resultCount,
    ...metadata
  }, newrelic);
};

/**
 * Track PagerDuty link clicks from leadership search
 *
 * @param {Object} metadata - Click metadata
 * @param {string} metadata.streamId - Delivery stream ID
 * @param {string} metadata.streamName - Delivery stream name
 * @param {string} metadata.workflowId - Workflow ID
 * @param {string} metadata.workflowType - Workflow type "IC"|"Leadership"
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * trackLeadershipSearchPagerDutyClick({
 *   streamId: 'ds-123',
 *   streamName: 'Production Stream',
 *   workflowId: 'wf-456',
 *   workflowType: 'IC'
 * });
 */
export const trackLeadershipSearchPagerDutyClick = (metadata = {}, newrelic = null) => {
  trackModernUIInteraction('Config.LeadershipSearch.PagerDutyClick', { ...metadata }, newrelic);
};

/**
 * Track workload CSV upload operations
 *
 * Records file selection, upload success/failure with processing metrics.
 *
 * @param {Object} metadata - Upload metadata
 * @param {string} metadata.operation - Operation type "FileSelected"|"UploadStarted"|"UploadSuccess"|"UploadFailed"
 * @param {number} metadata.fileSize - File size in bytes (optional)
 * @param {string} metadata.fileName - File name (optional)
 * @param {number} metadata.totalRecords - Total records processed (optional)
 * @param {number} metadata.createdRecords - Records created (optional)
 * @param {number} metadata.updatedRecords - Records updated (optional)
 * @param {number} metadata.failedRecords - Records failed (optional)
 * @param {string} metadata.error - Error message (optional)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track file selection
 * trackWorkloadUpload({
 *   operation: 'FileSelected',
 *   fileName: 'workloads.csv',
 *   fileSize: 102400
 * });
 *
 * @example
 * // Track successful upload
 * trackWorkloadUpload({
 *   operation: 'UploadSuccess',
 *   totalRecords: 1000,
 *   createdRecords: 500,
 *   updatedRecords: 500,
 *   failedRecords: 0
 * });
 */
export const trackWorkloadUpload = (metadata = {}, newrelic = null) => {
  trackModernUIInteraction(`Config.WorkloadUpload.${metadata.operation}`, { ...metadata }, newrelic);
};

// ========================================
// User Management Tracking Functions
// ========================================

/**
 * Track user management page access
 *
 * Records when a user accesses or is denied access to the User Management interface.
 * Includes admin user context for security auditing.
 *
 * @param {boolean} hasAccess - Whether deity privilege check passed
 * @param {Object} metadata - Additional tracking metadata
 * @param {string} metadata.adminEmail - Email of user attempting access
 * @param {string} metadata.reason - Reason for denial (if hasAccess is false)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track successful access
 * trackUserManagementAccess(true, {
 *   adminEmail: 'admin@coxautoinc.com'
 * });
 *
 * @example
 * // Track denied access
 * trackUserManagementAccess(false, {
 *   adminEmail: 'user@coxautoinc.com',
 *   reason: 'missing_deity_privilege'
 * });
 */
export const trackUserManagementAccess = (hasAccess, metadata = {}, newrelic = null) => {
  const action = hasAccess ? 'UserManagement.Access.Granted' : 'UserManagement.Access.Denied';
  trackModernUIInteraction(action, metadata, newrelic);
};

/**
 * Track user creation operations
 *
 * Records when an admin creates a new user account. Includes privilege and feature
 * toggles for security auditing.
 *
 * @param {string} userEmail - Email of the user being created
 * @param {Object} metadata - Additional tracking metadata
 * @param {string} metadata.adminEmail - Email of admin creating the user
 * @param {boolean} metadata.success - Whether creation succeeded
 * @param {Object} metadata.privileges - Privileges assigned to new user
 * @param {Object} metadata.features - Features enabled for new user
 * @param {string} metadata.error - Error message (if success is false)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track successful user creation
 * trackUserCreate('newuser@coxautoinc.com', {
 *   adminEmail: 'admin@coxautoinc.com',
 *   success: true,
 *   privileges: { editor: true, create: true },
 *   features: { modern_incidents: true }
 * });
 *
 * @example
 * // Track failed user creation
 * trackUserCreate('newuser@coxautoinc.com', {
 *   adminEmail: 'admin@coxautoinc.com',
 *   success: false,
 *   error: 'User already exists'
 * });
 */
export const trackUserCreate = (userEmail, metadata = {}, newrelic = null) => {
  trackModernUIInteraction('UserManagement.User.Create', {
    userEmail,
    ...metadata
  }, newrelic);
};

/**
 * Track user update operations
 *
 * Records when an admin updates an existing user account. Tracks which privileges
 * and features were modified for security auditing.
 *
 * @param {string} userEmail - Email of the user being updated
 * @param {Object} metadata - Additional tracking metadata
 * @param {string} metadata.adminEmail - Email of admin updating the user
 * @param {boolean} metadata.success - Whether update succeeded
 * @param {Array<string>} metadata.privilegesAdded - List of privileges added
 * @param {Array<string>} metadata.privilegesRemoved - List of privileges removed
 * @param {Array<string>} metadata.featuresAdded - List of features enabled
 * @param {Array<string>} metadata.featuresRemoved - List of features disabled
 * @param {string} metadata.error - Error message (if success is false)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track successful user update
 * trackUserUpdate('user@coxautoinc.com', {
 *   adminEmail: 'admin@coxautoinc.com',
 *   success: true,
 *   privilegesAdded: ['deity'],
 *   privilegesRemoved: [],
 *   featuresAdded: ['modern_status_page'],
 *   featuresRemoved: []
 * });
 */
export const trackUserUpdate = (userEmail, metadata = {}, newrelic = null) => {
  trackModernUIInteraction('UserManagement.User.Update', {
    userEmail,
    ...metadata
  }, newrelic);
};

/**
 * Track user deletion operations
 *
 * Records when an admin deletes a user account. Includes whether it was a single
 * or bulk deletion for security auditing.
 *
 * @param {string|Array<string>} userEmail - Email(s) of user(s) being deleted
 * @param {Object} metadata - Additional tracking metadata
 * @param {string} metadata.adminEmail - Email of admin performing deletion
 * @param {boolean} metadata.success - Whether deletion succeeded
 * @param {boolean} metadata.bulkDelete - Whether this was a bulk delete operation
 * @param {number} metadata.count - Number of users deleted (for bulk operations)
 * @param {string} metadata.error - Error message (if success is false)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track single user deletion
 * trackUserDelete('user@coxautoinc.com', {
 *   adminEmail: 'admin@coxautoinc.com',
 *   success: true,
 *   bulkDelete: false
 * });
 *
 * @example
 * // Track bulk user deletion
 * trackUserDelete(['user1@coxautoinc.com', 'user2@coxautoinc.com'], {
 *   adminEmail: 'admin@coxautoinc.com',
 *   success: true,
 *   bulkDelete: true,
 *   count: 2
 * });
 */
export const trackUserDelete = (userEmail, metadata = {}, newrelic = null) => {
  const isBulk = Array.isArray(userEmail);
  trackModernUIInteraction('UserManagement.User.Delete', {
    userEmail: isBulk ? userEmail.join(',') : userEmail,
    bulkDelete: isBulk,
    count: isBulk ? userEmail.length : 1,
    ...metadata
  }, newrelic);
};

/**
 * Track privilege toggle changes
 *
 * Records when an admin enables or disables a privilege for a user. Special tracking
 * for high-risk privileges like deity.
 *
 * @param {string} privilege - Name of the privilege being changed
 * @param {boolean} enabled - Whether privilege is being enabled or disabled
 * @param {Object} metadata - Additional tracking metadata
 * @param {string} metadata.userEmail - Email of user being modified
 * @param {string} metadata.adminEmail - Email of admin making the change
 * @param {string} metadata.riskLevel - Risk level of privilege (low|medium|high)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track deity privilege grant
 * trackPrivilegeChange('deity', true, {
 *   userEmail: 'user@coxautoinc.com',
 *   adminEmail: 'admin@coxautoinc.com',
 *   riskLevel: 'high'
 * });
 */
export const trackPrivilegeChange = (privilege, enabled, metadata = {}, newrelic = null) => {
  trackModernUIInteraction('UserManagement.Privilege.Change', {
    privilege,
    enabled,
    ...metadata
  }, newrelic);
};

/**
 * Track user search operations
 *
 * Records when an admin searches the user list. Helps understand how admins
 * find and manage users.
 *
 * @param {string} searchTerm - Search query entered
 * @param {Object} metadata - Additional tracking metadata
 * @param {number} metadata.resultCount - Number of users matching search
 * @param {string} metadata.adminEmail - Email of admin performing search
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * trackUserSearch('john.doe', {
 *   resultCount: 3,
 *   adminEmail: 'admin@coxautoinc.com'
 * });
 */
export const trackUserSearch = (searchTerm, metadata = {}, newrelic = null) => {
  trackModernUIInteraction('UserManagement.User.Search', {
    searchTerm,
    ...metadata
  }, newrelic);
};

/**
 * Track user list sort changes
 *
 * Records when an admin changes the sort field or direction in the user list.
 * Helps understand how admins prefer to organize user data.
 *
 * @param {string} sortField - Field being sorted (email|name|privileges|features)
 * @param {string} sortDirection - Sort direction (asc|desc)
 * @param {Object} metadata - Additional tracking metadata
 * @param {string} metadata.adminEmail - Email of admin changing sort
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * trackUserSortChange('email', 'asc', {
 *   adminEmail: 'admin@coxautoinc.com'
 * });
 */
export const trackUserSortChange = (sortField, sortDirection, metadata = {}, newrelic = null) => {
  trackModernUIInteraction('UserManagement.User.Sort', {
    sortField,
    sortDirection,
    ...metadata
  }, newrelic);
};

/**
 * Track modal open/close events
 *
 * Records when create/edit modals are opened or closed. Helps understand
 * user engagement with modal workflows.
 *
 * @param {string} modalType - Type of modal (create|edit)
 * @param {string} action - Action performed (open|close|cancel)
 * @param {Object} metadata - Additional tracking metadata
 * @param {string} metadata.userEmail - Email of user being edited (for edit modal)
 * @param {string} metadata.adminEmail - Email of admin opening modal
 * @param {boolean} metadata.hasUnsavedChanges - Whether user had unsaved changes on close
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 *
 * @example
 * // Track create modal open
 * trackUserModalInteraction('create', 'open', {
 *   adminEmail: 'admin@coxautoinc.com'
 * });
 *
 * @example
 * // Track edit modal close with unsaved changes
 * trackUserModalInteraction('edit', 'close', {
 *   userEmail: 'user@coxautoinc.com',
 *   adminEmail: 'admin@coxautoinc.com',
 *   hasUnsavedChanges: true
 * });
 */
export const trackUserModalInteraction = (modalType, action, metadata = {}, newrelic = null) => {
  trackModernUIInteraction('UserManagement.Modal.Interaction', {
    modalType,
    action,
    ...metadata
  }, newrelic);
};

/**
 * Track past incidents section page view
 *
 * Records when a user views the past incidents section. Includes count of
 * past incidents currently displayed. Should be called when the component
 * finishes loading.
 *
 * @param {number} incidentCount - Number of past incidents currently displayed
 * @param {Object} options - Additional tracking options (reserved for future use)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 */
export const trackPastIncidentsSectionView = (incidentCount, options = {}, newrelic = null) => {
  const metadata = {
    incidentCount,
    uiType: 'modern',
    ...options,
  };

  trackModernUIInteraction('ModernUI.PastIncidents.View', metadata, newrelic);
};

/**
 * Track past incidents "Load More" action
 *
 * Records when a user triggers loading more past incidents (either by
 * clicking a button or via infinite scroll).
 *
 * @param {Object} options - Additional tracking options (reserved for future use)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 */
export const trackPastIncidentsLoadMore = (options = {}, newrelic = null) => {
  const metadata = {
    uiType: 'modern',
    ...options,
  };

  trackModernUIInteraction('ModernUI.PastIncidents.LoadMore', metadata, newrelic);
};

/**
 * Track a click on the Past Incidents header's PIR Library link (US2135594)
 *
 * @param {Object} options - Additional tracking options (reserved for future use)
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 */
export const trackPastIncidentsPirLibraryClick = (options = {}, newrelic = null) => {
  const metadata = {
    uiType: 'modern',
    ...options,
  };

  trackModernUIInteraction('ModernUI.PastIncidents.PirLibrary.Click', metadata, newrelic);
};

/**
 * Track a click on a Past Incidents row's or card's Exec Summary icon (US2135577).
 * Consistent with the PIR Library link (US2135594). `source` is `list_row` or `card`.
 *
 * @param {Object} options - { incidentId, source }
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 */
export const trackPastIncidentsExecSummaryClick = (options = {}, newrelic = null) => {
  const metadata = {
    uiType: 'modern',
    ...options,
  };

  trackModernUIInteraction('ModernUI.PastIncidents.ExecSummary.Click', metadata, newrelic);
};

/**
 * Track a resolved incident rendered without a resolve timestamp (US2072496).
 *
 * This is a DATA-DEFECT signal, not an engagement metric. A record marked
 * resolved should always carry an incidentEnd, so the expected volume is zero.
 * The affected surfaces render "Resolved —" rather than falling back to the start
 * time; this event is what makes that state discoverable instead of relying on
 * someone noticing an em dash.
 *
 * Fires once per affected incident per mount, from whichever surface rendered it.
 * Do NOT alert on a single occurrence — a spike above a zero baseline is the
 * meaningful signal.
 *
 * @param {string} incidentId - Problem number (PRB/PRBT/INC), not PII
 * @param {Object} options - `surface` ('list_row' | 'card' | 'detail_header') and
 *                           `section` where applicable
 * @param {Object} newrelic - NewRelic browser agent instance (optional; falls back
 *                            to window.newrelic)
 */
export const trackMissingResolveDate = (incidentId, options = {}, newrelic = null) => {
  if (!incidentId) return;

  trackModernUIInteraction('ModernUI.PastIncidents.MissingResolveDate', {
    incidentId,
    uiType: 'modern',
    ...options,
  }, newrelic);
};

export const trackJoinBridgeClick = (incidentId, outcome, httpStatus = null, newrelic = null) => {
  trackModernUIInteraction('ModernUI.Incident.JoinBridge', {
    incidentId,
    outcome: outcome ?? 'unknown',
    httpStatus,
  }, newrelic);
};

// ========================================
// Past Incidents Date Filter Tracking (US1947404)
// ========================================

/**
 * Track when the date filter menu is opened.
 *
 * @param {Object} options
 * @param {boolean} options.hasActiveFilter - Whether a filter is already applied
 * @param {string|null} options.currentMode - The currently applied filter mode
 * @param {Object} newrelic
 */
export const trackPastIncidentsDateFilterOpen = (options = {}, newrelic = null) => {
  trackModernUIInteraction('ModernUI.PastIncidents.DateFilter.Open', {
    hasActiveFilter: options.hasActiveFilter ?? false,
    currentMode: options.currentMode ?? null,
  }, newrelic);
};

/**
 * Track when the user applies a date filter.
 *
 * @param {Object} options
 * @param {string|null} options.filterMode - Preset id ('last7'|'last14'|'last30'|'last90'|'custom') or null
 * @param {boolean} options.hasStartDate - Whether a start date is set (custom mode)
 * @param {boolean} options.hasEndDate - Whether an end date is set (custom mode)
 * @param {string|null} options.rangeStartDate - ISO date string (YYYY-MM-DD) of the computed start bound
 * @param {string|null} options.rangeEndDate - ISO date string (YYYY-MM-DD) of the computed end bound
 * @param {Object} newrelic
 */
export const trackPastIncidentsDateFilterApply = (options = {}, newrelic = null) => {
  trackModernUIInteraction('ModernUI.PastIncidents.DateFilter.Apply', {
    filterMode: options.filterMode ?? null,
    hasStartDate: options.hasStartDate ?? false,
    hasEndDate: options.hasEndDate ?? false,
    rangeStartDate: options.rangeStartDate ?? null,
    rangeEndDate: options.rangeEndDate ?? null,
  }, newrelic);
};

/**
 * Track when the user resets (clears) the date filter.
 *
 * @param {Object} options
 * @param {string|null} options.previousMode - The filter mode that was active before reset
 * @param {Object} newrelic
 */
export const trackPastIncidentsDateFilterReset = (options = {}, newrelic = null) => {
  trackModernUIInteraction('ModernUI.PastIncidents.DateFilter.Reset', {
    previousMode: options.previousMode ?? null,
  }, newrelic);
};

/**
 * Track when the user cancels (discards pending changes) the date filter.
 *
 * @param {Object} options
 * @param {boolean} options.hasActiveFilter - Whether a filter remains active after cancel
 * @param {string|null} options.currentMode - The currently applied (unchanged) filter mode
 * @param {Object} newrelic
 */
export const trackPastIncidentsDateFilterCancel = (options = {}, newrelic = null) => {
  trackModernUIInteraction('ModernUI.PastIncidents.DateFilter.Cancel', {
    hasActiveFilter: options.hasActiveFilter ?? false,
    currentMode: options.currentMode ?? null,
  }, newrelic);
};

// ========================================
// Past Incidents Engagement Tracking (US1947406)
// ========================================

/**
 * Track past incident detail page view.
 *
 * Fires on mount of the US1947406 past incident detail component.
 * viewport, darkMode, and timestamp are auto-enriched.
 *
 * @param {string} incidentId - Problem number
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 */
export const trackPastIncidentDetailView = (incidentId, newrelic = null) => {
  if (!incidentId) return;
  trackModernUIInteraction('ModernUI.PastIncidents.DetailView', { incidentId }, newrelic);
};

/**
 * Track time spent on a past incident detail page.
 *
 * Fires on unmount of the US1947406 past incident detail component so the
 * full session duration is captured before navigation.
 *
 * @param {string} incidentId - Problem number
 * @param {number} durationMs - Total time the detail page was open in ms
 * @param {Object} newrelic - NewRelic browser agent instance (optional)
 */
export const trackPastIncidentTimeSpent = (incidentId, durationMs, newrelic = null) => {
  if (!incidentId) return;
  trackModernUIInteraction('ModernUI.PastIncidents.TimeSpent', {
    incidentId,
    durationMs: durationMs ?? 0,
  }, newrelic);
};

// ========================================
// Past Incidents Filter Tracking (US1984532)
// ========================================

export const trackPastIncidentsPriorityFilterApply = (options = {}, newrelic = null) => {
  trackModernUIInteraction('ModernUI.PastIncidents.PriorityFilter.Apply', {
    priorities: options.priorities || [],
    priorityCount: (options.priorities || []).length,
  }, newrelic);
};

export const trackPastIncidentsPriorityFilterReset = (options = {}, newrelic = null) => {
  trackModernUIInteraction('ModernUI.PastIncidents.PriorityFilter.Reset', {
    previousPriorities: options.previousPriorities || [],
  }, newrelic);
};

export const trackPastIncidentsBrandFilterApply = (options = {}, newrelic = null) => {
  trackModernUIInteraction('ModernUI.PastIncidents.BrandFilter.Apply', {
    brands: options.brands || [],
    brandCount: (options.brands || []).length,
  }, newrelic);
};

export const trackPastIncidentsBrandFilterReset = (options = {}, newrelic = null) => {
  trackModernUIInteraction('ModernUI.PastIncidents.BrandFilter.Reset', {
    previousBrands: options.previousBrands || [],
  }, newrelic);
};

// US1984532: Unified filter apply — all active dimensions in one event.
// Use this for dashboard queries instead of joining 3 separate events.
export const trackPastIncidentsFilterApply = (options = {}, newrelic = null) => {
  trackModernUIInteraction('ModernUI.PastIncidents.Filter.Apply', {
    dimensionCount: options.dimensionCount || 0,
    hasDate: options.hasDate || false,
    dateMode: options.dateMode || null,
    priorityCount: options.priorityCount || 0,
    priorities: options.priorities || [],
    brandCount: options.brandCount || 0,
    brands: options.brands || [],
  }, newrelic);
};

// US1984532: Fired when a filtered query returns zero results.
// Helps identify filter combinations that produce no data.
export const trackPastIncidentsFilterEmptyResult = (options = {}, newrelic = null) => {
  trackModernUIInteraction('ModernUI.PastIncidents.Filter.EmptyResult', {
    priorityCount: options.priorityCount || 0,
    brandCount: options.brandCount || 0,
    hasDate: options.hasDate || false,
    dimensionCount: options.dimensionCount || 0,
  }, newrelic);
};
